import { useContext, useEffect, useState } from "react";
import { Avatar, Box, Drawer, IconButton, Typography } from "@mui/material";
import { Menu, Logout, SpaceDashboard, Storefront, EmojiEvents, Print, AccountBalanceWallet, Settings, PeopleAlt, History, Loyalty } from "@mui/icons-material";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { DataContext } from "../../context/DataContext";
import { consoleColors, headingCase } from "../admin/consoleTheme";
import { formatAmount } from "../../include/money";
import ClubHome from "./ClubHome";
import ClubStores from "./ClubStores";
import ClubDraws, { ClubDrawForm } from "./ClubDraws";
import ClubWallet from "./ClubWallet";
import PrintRequests, { PrintRequestForm } from "../printcard/PrintRequests";
import StoreLoyaltySetting from "../store/LoyaltySetting";
import LuckyDrawResult from "../printcard/LuckyDrawResult";
import LuckyDrawWinner from "../printcard/LuckyDrawWinner";
import LaunchDraw from "../admin/LaunchDraw";
import Users, { UserForm } from "../users/Users";
import AuditTrail from "../admin/AuditTrail";
import ClubLoyaltyHistory from "./ClubLoyaltyHistory";
import { CLUB_MENU_CATALOG, filterGroups, firstAllowedPath, isOwner, pathAllowed, staffOf } from "../../include/staffAccess";

const groups = [
  {
    label: "Overview",
    items: [{ to: "/club/home", menu: "home", label: "Dashboard", icon: SpaceDashboard }],
  },
  {
    label: "Club",
    items: [
      { to: "/club/stores", menu: "stores", label: "Stores", icon: Storefront },
      { to: "/club/luckydraw", menu: "luckydraw", label: "Lucky Draw Coupons", icon: EmojiEvents },
      { to: "/club/print", menu: "print", label: "Print Loyalty Cards", icon: Print },
      { to: "/club/loyalty", menu: "loyalty", label: "Loyalty Settings", icon: Settings },
      { to: "/club/loyalty-history", menu: "loyaltyhistory", label: "Loyalty history", icon: Loyalty },
      { to: "/club/wallet", menu: "wallet", label: "Wallet", icon: AccountBalanceWallet },
      { to: "/club/audit", menu: "audit", label: "Audit trail", icon: History },
      { to: "/club/users", menu: "users", label: "Users", icon: PeopleAlt },
    ],
  },
];

const pageMeta = {
  "/club/home": ["Dashboard", "Stores, draws, and print requests for this club"],
  "/club/stores": ["Stores", "Stores that belong to this club"],
  "/club/luckydraw": ["Lucky Draw Coupons", "Create, launch, and view results"],
  "/club/luckydraw/new": ["Create Lucky Draw Coupons", "Shown in every store in this club"],
  "/club/print": ["Print Loyalty Cards", "Request printed loyalty cards from this club"],
  "/club/print/new": ["Request Printed Cards", "Number of cards and wallet cut"],
  "/club/users": ["Users", "Login ids, passwords, and menu permission"],
  "/club/users/new": ["Add user", "Login id, password, and menus this user can open"],
  "/club/loyalty": ["Loyalty settings", "Default for stores in this club"],
  "/club/loyalty-history": ["Loyalty history", "Points used across stores in this club"],
  "/club/wallet": ["Wallet", "Club wallet used for print requests"],
  "/club/audit": ["Audit trail", "Sales, wallet, coupons, and print movements for this club"],
  "/club/luckydraw-winner": ["Lucky Draw Coupons Winner", "The customer picked for this draw"],
};

const SIDEBAR = 252;

function SidebarContent({ name, wallet, navGroups, staffLabel, onNavigate, onLogout }) {
  const location = useLocation();
  const initial = (name || "C").trim().charAt(0).toUpperCase();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", background: consoleColors.sidebar, color: "#fff" }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Typography sx={{ fontSize: 13, letterSpacing: 1.4, color: consoleColors.gold, fontWeight: 700 }}>CASHI</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, mt: 0.2 }}>Club</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 2 }}>
        {navGroups.map((group) => (
          <Box key={group.label} sx={{ mb: 2 }}>
            <Typography sx={{ px: 1.2, mb: 0.6, fontSize: 11, letterSpacing: 0.8, color: consoleColors.navMuted, fontWeight: 700 }}>
              {group.label.toUpperCase()}
            </Typography>
            {group.items.map((item) => {
              const active = location.pathname === item.to || (item.to !== "/club/home" && location.pathname.startsWith(`${item.to}/`));
              const Icon = item.icon;
              return (
                <Box
                  key={item.to}
                  onClick={() => onNavigate(item.to)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 1.2,
                    py: 0.9,
                    mb: 0.3,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    color: active ? "#fff" : consoleColors.navText,
                    background: active ? consoleColors.navActive : "transparent",
                    "&:hover": { background: active ? consoleColors.navActive : "rgba(255,255,255,0.06)" },
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: active ? consoleColors.accent : consoleColors.navMuted }} />
                  <Typography sx={{ fontSize: 14, fontWeight: active ? 650 : 500 }}>{headingCase(item.label)}</Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 1.2 }}>
        <Avatar sx={{ width: 34, height: 34, bgcolor: consoleColors.accent, fontSize: 14 }}>{initial}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 650 }}>{name}</Typography>
          <Typography sx={{ fontSize: 12, color: consoleColors.navMuted }}>₹{formatAmount(wallet)} · {staffLabel}</Typography>
        </Box>
        <IconButton onClick={onLogout} size="small" sx={{ color: "#cbd5e1" }} aria-label="Logout">
          <Logout sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function ClubShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clubData, setClubData } = useContext(DataContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const name = clubData?.club_info?.title || "Club";
  const wallet = clubData?.club_info?.wallet_amount || 0;
  const staff = staffOf(clubData);
  const navGroups = filterGroups(groups, clubData);
  const homePath = firstAllowedPath(clubData, groups, "/club/home");
  const staffLabel = staff && !staff.is_owner ? staff.username : "Club admin";
  const [title, subtitle] = pageMeta[location.pathname]
    || (location.pathname.startsWith("/club/users/") && location.pathname.endsWith("/edit") ? ["Edit user", "Login id, password, and menus this user can open"] : null)
    || (location.pathname.startsWith("/club/luckydraw/") && location.pathname.endsWith("/edit") ? ["Edit Lucky Draw Coupons", "Update title, schedule, and images"] : null)
    || (location.pathname.startsWith("/club/luckydraw/") && location.pathname.includes("/launch") ? ["Launch Draw", "Countdown, then the winner"] : null)
    || (location.pathname.startsWith("/club/luckydraw/") ? ["Lucky Draw Coupons", "Result, issued customers, and winners"] : ["Club", ""]);

  useEffect(() => {
    if (isOwner(clubData) || pathAllowed(location.pathname, clubData, CLUB_MENU_CATALOG)) return;
    if (homePath && homePath !== location.pathname) navigate(homePath, { replace: true });
  }, [clubData, homePath, location.pathname, navigate]);

  const logout = () => {
    setClubData({});
    sessionStorage.removeItem("clubData");
    navigate("/club", { replace: true });
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: consoleColors.page }}>
      <Box sx={{ width: SIDEBAR, flexShrink: 0, display: { xs: "none", md: "block" } }}>
        <Box sx={{ position: "fixed", width: SIDEBAR, height: "100vh" }}>
          <SidebarContent name={staff?.name || name} wallet={wallet} navGroups={navGroups} staffLabel={staffLabel} onNavigate={go} onLogout={logout} />
        </Box>
      </Box>

      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { md: "none" }, "& .MuiDrawer-paper": { width: SIDEBAR, border: 0 } }}>
        <SidebarContent name={staff?.name || name} wallet={wallet} navGroups={navGroups} staffLabel={staffLabel} onNavigate={go} onLogout={logout} />
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: 10, background: consoleColors.pageBar, backdropFilter: "blur(8px)", borderBottom: `1px solid ${consoleColors.line}`, px: { xs: 2, md: 3 }, py: 1.6, display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => setMobileOpen(true)} sx={{ display: { md: "none" } }} aria-label="Open menu">
            <Menu />
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: consoleColors.ink, lineHeight: 1.2 }}>{headingCase(title)}</Typography>
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{headingCase(subtitle)}</Typography>
          </Box>
        </Box>

        <Routes>
          <Route path="home" element={<ClubHome />} />
          <Route path="stores" element={<ClubStores />} />
          <Route path="luckydraw-winner" element={<LuckyDrawWinner />} />
          <Route path="luckydraw/new" element={<ClubDrawForm />} />
          <Route path="luckydraw/:drawId/edit" element={<ClubDrawForm />} />
          <Route path="luckydraw/:drawId/launch" element={<LaunchDraw source="club" />} />
          <Route path="luckydraw/:drawId" element={<LuckyDrawResult />} />
          <Route path="luckydraw" element={<ClubDraws />} />
          <Route path="print/new" element={<PrintRequestForm source="club" />} />
          <Route path="print" element={<PrintRequests source="club" />} />
          <Route path="loyalty" element={<StoreLoyaltySetting source="club" />} />
          <Route path="loyalty-history" element={<ClubLoyaltyHistory />} />
          <Route path="wallet" element={<ClubWallet />} />
          <Route path="audit" element={<AuditTrail source="club" />} />
          <Route path="users/new" element={<UserForm source="club" />} />
          <Route path="users/:userId/edit" element={<UserForm source="club" />} />
          <Route path="users" element={<Users source="club" />} />
          <Route path="*" element={<Navigate to={homePath || "/club/home"} replace />} />
        </Routes>
      </Box>
    </Box>
  );
}
